import { ApiResult, HttpClient } from "../http.js";
import {
  BonusSchemeSettingsDto,
  ApplyAutoGenerationPayload,
  AutoGenerationResultDto,
  ServiceOrderSettingsDto,
  ServiceVisitNoticePreviewDto,
  UpdateServiceOrderSettingsPayload,
  QuoteFollowUpSettingsDto,
  UpdateQuoteFollowUpSettingsPayload,
  QuotePdfDefaultsSettingsDto,
  UpdateQuotePdfDefaultsSettingsPayload,
  CaseNumberConfigDto,
  CommunicationSettingsDto,
  EconomicOptionDto,
  EconomySettingsDto,
  UpdateEconomySettingsPayload,
  EconomicRecommendedSetupDto,
  EconomicSetupOptionsDto,
  IntegrationSettingsDto,
  InvoiceNumberConfigDto,
  ProjectWorkflowConfigDto,
  QuoteNumberConfigDto,
  UpdateBonusSchemeSettingsPayload,
  UpdateCaseNumberConfigPayload,
  UpdateInvoiceNumberConfigPayload,
  UpdateQuoteNumberConfigPayload,
  UpdateProjectWorkflowConfigPayload,
  TimeRegistrationConfigDto,
  UpdateTimeRegistrationConfigPayload,
  KlsSettingsDto,
  UpdateKlsSettingsPayload,
} from "../types/settings.js";
import {
  CustomerScoreSettingsDto,
  UpdateCustomerScoreSettingsPayload,
} from "../types/customer-scores.js";
import { AnthropicByoStateDto } from "../types/blueprintCredits.js";
import {
  CreateProjectStatusPayload,
  ProjectStatusDefinitionDto,
  UpdateProjectStatusPayload,
} from "../types/project-statuses.js";
import {
  CreateTeamPayload,
  TeamDto,
  UpdateTeamPayload,
} from "../types/teams.js";

export interface CompanyLogoDto {
  blobPath: string;
  signedReadUrl: string;
}

/**
 * PUT body for `/api/settings/communication`. The module-level `enabled` flag is
 * omitted on purpose — it is owned by the Marketplace activation flow, not this
 * endpoint.
 */
export interface UpdateCommunicationSettingsPayload {
  messagesEnabled: boolean;
  customerPortalEnabled: boolean;
  customerNotificationsEnabled: boolean;
  smsNotificationsEnabled: boolean;
  smsSenderName?: string | null;
  smsMessageTemplate?: string | null;
}

export interface RegisterNemHandelPayload {
  /** 13-digit GLN/EAN number the company receives e-faktura on. */
  glnNumber: string;
  companyName: string;
}

/** Body for `POST /api/settings/integrations/connection`. */
export interface SetIntegrationConnectionPayload {
  /** `dinero` | `economic` | `googleCalendar` | `danlon` (case-insensitive). */
  provider: string;
  connect: boolean;
  /** Key Vault URI — required (with one of the others) when `connect` is true. */
  refreshTokenUri?: string | null;
  accessTokenUri?: string | null;
  grantTokenUri?: string | null;
}

/** Body for `PUT /api/settings/integrations/economic`. */
export interface UpdateEconomicConfigPayload {
  defaultJournalNumber?: number | null;
  /** The bank account for customer payments ("Bankkonto for indbetalinger"). */
  defaultContraAccount?: number | null;
  categoryAccountMapping?: Record<string, number> | null;
  /** Omit (or null) to leave the stored layout unchanged. */
  defaultLayoutNumber?: number | null;
  /**
   * Omit (or null) to leave the stored value unchanged; pass an empty string
   * to clear it.
   */
  defaultInvoiceProductNumber?: string | null;
  /** Default supplier group for "Opret i e-conomic". Omit (or null) to leave it unchanged. */
  defaultSupplierGroupNumber?: number | null;
  /** Default supplier payment terms for "Opret i e-conomic". Omit (or null) to leave it unchanged. */
  defaultSupplierPaymentTermsNumber?: number | null;
  /** True empties the default supplier group ("Ingen standard"); wins over the number. */
  clearDefaultSupplierGroupNumber?: boolean;
  /** True empties the default supplier payment terms; wins over the number. */
  clearDefaultSupplierPaymentTermsNumber?: boolean;
  /** "Udgiftskonto for leverandørfakturaer". Omit (or null) to leave it unchanged. */
  defaultSupplierExpenseAccount?: number | null;
  /** True empties the supplier expense account; wins over the number. */
  clearDefaultSupplierExpenseAccount?: boolean;
  /**
   * Display names of the values being saved ("5820 – Bankkonto"), keyed by setting. Merged into
   * the stored labels; omit to leave them unchanged, an empty string removes one.
   */
  labels?: Record<string, string> | null;
}

export class SettingsApi {
  constructor(private readonly http: HttpClient) {}

  integrations(): Promise<ApiResult<IntegrationSettingsDto>> {
    return this.http.get<IntegrationSettingsDto>("/api/settings/integrations");
  }

  /**
   * GET /api/settings/integrations/economic/options
   * Reads journals, accounts and products from the org's e-conomic agreement so the
   * settings UI can fill the config in. Read-only; 409 when e-conomic is not connected
   * or the agreement cannot be read.
   */
  economicSetupOptions(): Promise<ApiResult<EconomicSetupOptionsDto>> {
    return this.http.get<EconomicSetupOptionsDto>(
      "/api/settings/integrations/economic/options",
    );
  }

  /**
   * GET /api/settings/integrations/economic/supplier-groups — the agreement's supplier groups as
   * "number — name" options, for the default used by "Opret i e-conomic".
   */
  economicSupplierGroups(): Promise<ApiResult<EconomicOptionDto[]>> {
    return this.http.get<EconomicOptionDto[]>(
      "/api/settings/integrations/economic/supplier-groups",
    );
  }

  /**
   * GET /api/settings/integrations/economic/recommended — "Brug anbefalet opsætning": a proposed
   * value, label, reason and confidence for each e-conomic setting, read from the org's own
   * agreement. Read-only; a null value means "vælg selv". 409 when e-conomic is not connected or
   * cannot be read.
   */
  economicRecommendedSetup(): Promise<ApiResult<EconomicRecommendedSetupDto>> {
    return this.http.get<EconomicRecommendedSetupDto>(
      "/api/settings/integrations/economic/recommended",
    );
  }

  /** GET /api/settings/integrations/economic/payment-terms — the agreement's payment terms as options. */
  economicPaymentTerms(): Promise<ApiResult<EconomicOptionDto[]>> {
    return this.http.get<EconomicOptionDto[]>(
      "/api/settings/integrations/economic/payment-terms",
    );
  }

  /**
   * PUT /api/settings/integrations/economic
   * Updates the org's e-conomic configuration and returns the full integration
   * settings. `defaultJournalNumber`, `defaultContraAccount` and
   * `categoryAccountMapping` are replaced outright; the two optional fields are
   * left untouched when omitted. Config fields this endpoint does not own (pull
   * cursors, customer-group defaults, …) are preserved server-side.
   */
  updateEconomicConfig(
    payload: UpdateEconomicConfigPayload,
  ): Promise<ApiResult<IntegrationSettingsDto>> {
    return this.http.put<IntegrationSettingsDto>(
      "/api/settings/integrations/economic",
      { body: payload },
    );
  }

  /**
   * POST /api/settings/integrations/connection
   * Records or clears an integration's connection state for the active org.
   *
   * Connecting REQUIRES at least one Key Vault URI — the backend rejects raw tokens and
   * rejects a connect with no URI at all. Clients that hold tokens locally (no Key Vault
   * write) can therefore only use this to DISCONNECT (`connect: false`).
   */
  setIntegrationConnection(
    payload: SetIntegrationConnectionPayload,
  ): Promise<ApiResult<IntegrationSettingsDto>> {
    return this.http.post<IntegrationSettingsDto>(
      "/api/settings/integrations/connection",
      { body: payload },
    );
  }

  // ─── Case number ──────────────────────────────────────────────────────

  /**
   * GET /api/settings/case-number
   * Returns the case-number config (defaults when no row exists). Replaces the
   * legacy Firestore `settings.caseNumber` read.
   */
  getCaseNumber(): Promise<ApiResult<CaseNumberConfigDto>> {
    return this.http.get<CaseNumberConfigDto>("/api/settings/case-number");
  }

  /**
   * PUT /api/settings/case-number
   * Persists prefix + next-number floor. `nextNumber` must be >= 1.
   */
  updateCaseNumber(
    payload: UpdateCaseNumberConfigPayload,
  ): Promise<ApiResult<CaseNumberConfigDto>> {
    return this.http.put<CaseNumberConfigDto>("/api/settings/case-number", {
      body: payload,
    });
  }

  // ─── Invoice number ───────────────────────────────────────────────────

  /**
   * GET /api/settings/invoice-number
   * Returns the invoice-number config (defaults when no row exists). Replaces
   * the legacy Firestore `settings.invoiceNumber` read.
   */
  getInvoiceNumber(): Promise<ApiResult<InvoiceNumberConfigDto>> {
    return this.http.get<InvoiceNumberConfigDto>("/api/settings/invoice-number");
  }

  /**
   * PUT /api/settings/invoice-number
   * Persists prefix + next-number floor. `nextNumber` must be >= 1.
   */
  updateInvoiceNumber(
    payload: UpdateInvoiceNumberConfigPayload,
  ): Promise<ApiResult<InvoiceNumberConfigDto>> {
    return this.http.put<InvoiceNumberConfigDto>("/api/settings/invoice-number", {
      body: payload,
    });
  }

  /**
   * POST /api/settings/invoice-number/consume
   * Atomically reserves and returns the next invoice number (int). Combine with
   * the configured prefix from getInvoiceNumber() for a display number.
   */
  consumeNextInvoiceNumber(): Promise<ApiResult<number>> {
    return this.http.post<number>("/api/settings/invoice-number/consume", {});
  }

  // ─── Quote number ─────────────────────────────────────────────────────

  /**
   * GET /api/settings/quote-number
   * Returns the quote-number config (defaults when no row exists). Use it to
   * show the number the server would allocate next; the create endpoints do the
   * allocating, so nothing here needs to be sent back on create.
   */
  getQuoteNumber(): Promise<ApiResult<QuoteNumberConfigDto>> {
    return this.http.get<QuoteNumberConfigDto>("/api/settings/quote-number");
  }

  /**
   * PUT /api/settings/quote-number
   * Persists prefix + next-number floor. `nextNumber` must be >= 1.
   */
  updateQuoteNumber(
    payload: UpdateQuoteNumberConfigPayload,
  ): Promise<ApiResult<QuoteNumberConfigDto>> {
    return this.http.put<QuoteNumberConfigDto>("/api/settings/quote-number", {
      body: payload,
    });
  }

  /**
   * POST /api/settings/quote-number/consume
   * Atomically reserves and returns the next quote number (int). Rarely needed
   * from a client — creating a quote without a number allocates one server-side.
   */
  consumeNextQuoteNumber(): Promise<ApiResult<number>> {
    return this.http.post<number>("/api/settings/quote-number/consume", {});
  }

  // ─── Project workflow ─────────────────────────────────────────────────

  /**
   * GET /api/settings/project-workflow
   * Returns the project-workflow config (defaults when no row exists). Replaces
   * the legacy Firestore `settings.projectWorkflow` read.
   */
  getProjectWorkflow(): Promise<ApiResult<ProjectWorkflowConfigDto>> {
    return this.http.get<ProjectWorkflowConfigDto>(
      "/api/settings/project-workflow",
    );
  }

  /**
   * PUT /api/settings/project-workflow
   * Overwrites the whole row — both fields must be sent together.
   */
  updateProjectWorkflow(
    payload: UpdateProjectWorkflowConfigPayload,
  ): Promise<ApiResult<ProjectWorkflowConfigDto>> {
    return this.http.put<ProjectWorkflowConfigDto>(
      "/api/settings/project-workflow",
      { body: payload },
    );
  }

  // ─── Project statuses ─────────────────────────────────────────────────

  /**
   * GET /api/settings/project-statuses
   * Returns every status for the org including hidden ones, ordered by sortOrder.
   * Hidden statuses must still be returned — a project may hold one, and the badge
   * has to render its name.
   */
  listProjectStatuses(): Promise<ApiResult<ProjectStatusDefinitionDto[]>> {
    return this.http.get<ProjectStatusDefinitionDto[]>("/api/settings/project-statuses");
  }

  createProjectStatus(
    payload: CreateProjectStatusPayload,
  ): Promise<ApiResult<ProjectStatusDefinitionDto>> {
    return this.http.post<ProjectStatusDefinitionDto>("/api/settings/project-statuses", {
      body: payload,
    });
  }

  /** PUT overwrites the whole row — send every field, as with project-workflow. */
  updateProjectStatus(
    id: string,
    payload: UpdateProjectStatusPayload,
  ): Promise<ApiResult<ProjectStatusDefinitionDto>> {
    return this.http.put<ProjectStatusDefinitionDto>(
      `/api/settings/project-statuses/${id}`,
      { body: payload },
    );
  }

  deleteProjectStatus(id: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/settings/project-statuses/${id}`);
  }

  // ─── Teams ────────────────────────────────────────────────────────────

  /**
   * GET /api/settings/teams
   * Every team for the org **including archived ones**, ordered by sortOrder. Archived teams
   * must still be returned: a membership may point at one, and the settings list has to let an
   * admin unarchive it.
   */
  listTeams(): Promise<ApiResult<TeamDto[]>> {
    return this.http.get<TeamDto[]>("/api/settings/teams");
  }

  createTeam(payload: CreateTeamPayload): Promise<ApiResult<TeamDto>> {
    return this.http.post<TeamDto>("/api/settings/teams", { body: payload });
  }

  /** PUT overwrites the whole row — send every field, as with project-statuses. */
  updateTeam(id: string, payload: UpdateTeamPayload): Promise<ApiResult<TeamDto>> {
    return this.http.put<TeamDto>(`/api/settings/teams/${id}`, { body: payload });
  }

  /**
   * DELETE /api/settings/teams/{id}
   * 409 with a Danish `detail` when memberships still point at the team
   * ("Teamet bruges af N medarbejder(e) — arkivér det i stedet for at slette."). That is the
   * only guard: a team with `memberCount === 0` can be hard-deleted, standard (carrying a
   * `legacyTypeKey`) or custom alike. Surface that detail; do not replace it with a generic
   * message.
   */
  deleteTeam(id: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/settings/teams/${id}`);
  }

  // ─── Time registration ────────────────────────────────────────────────

  /**
   * GET /api/settings/time-registration
   * Returns the time-registration mode, defaulting to "StartStop" when the
   * company has never chosen.
   */
  getTimeRegistration(): Promise<ApiResult<TimeRegistrationConfigDto>> {
    return this.http.get<TimeRegistrationConfigDto>(
      "/api/settings/time-registration",
    );
  }

  /** PUT /api/settings/time-registration */
  updateTimeRegistration(
    payload: UpdateTimeRegistrationConfigPayload,
  ): Promise<ApiResult<TimeRegistrationConfigDto>> {
    return this.http.put<TimeRegistrationConfigDto>(
      "/api/settings/time-registration",
      { body: payload },
    );
  }

  // ─── KLS default trade ────────────────────────────────────────────────

  /**
   * GET /api/settings/kls
   * The company's default KLS trade. `trade` is null when never chosen (use "electrician").
   * Stored in the company settings blob (`settings.kls.trade`), so no copy is needed.
   */
  getKls(): Promise<ApiResult<KlsSettingsDto>> {
    return this.http.get<KlsSettingsDto>("/api/settings/kls");
  }

  /** PUT /api/settings/kls — requires settings.editCompany. */
  updateKls(payload: UpdateKlsSettingsPayload): Promise<ApiResult<KlsSettingsDto>> {
    return this.http.put<KlsSettingsDto>("/api/settings/kls", { body: payload });
  }

  // ─── Bonus scheme ─────────────────────────────────────────────────────

  /**
   * GET /api/settings/bonus-scheme
   * Returns the bonus-scheme settings (defaults when no row exists). Replaces
   * the legacy Firestore `settings.bonusScheme` read.
   */
  getBonusScheme(): Promise<ApiResult<BonusSchemeSettingsDto>> {
    return this.http.get<BonusSchemeSettingsDto>("/api/settings/bonus-scheme");
  }

  /**
   * PUT /api/settings/bonus-scheme
   * Full round-trip of the bonus-scheme settings.
   */
  updateBonusScheme(
    payload: UpdateBonusSchemeSettingsPayload,
  ): Promise<ApiResult<BonusSchemeSettingsDto>> {
    return this.http.put<BonusSchemeSettingsDto>("/api/settings/bonus-scheme", {
      body: payload,
    });
  }

  /**
   * GET /api/settings/quote-follow-up
   * The org's follow-up mail text, plus the built-in defaults used when it is unset.
   */
  getQuoteFollowUp(): Promise<ApiResult<QuoteFollowUpSettingsDto>> {
    return this.http.get<QuoteFollowUpSettingsDto>("/api/settings/quote-follow-up");
  }

  /**
   * PUT /api/settings/quote-follow-up
   * Full round-trip of the follow-up mail text. Send null to fall back to the default.
   */
  updateQuoteFollowUp(
    payload: UpdateQuoteFollowUpSettingsPayload,
  ): Promise<ApiResult<QuoteFollowUpSettingsDto>> {
    return this.http.put<QuoteFollowUpSettingsDto>("/api/settings/quote-follow-up", {
      body: payload,
    });
  }

  /** GET /api/settings/service-orders — lead days and the customer notice flag (defaults 30 / off). */
  getServiceOrderSettings(): Promise<ApiResult<ServiceOrderSettingsDto>> {
    return this.http.get<ServiceOrderSettingsDto>("/api/settings/service-orders");
  }

  /** PUT /api/settings/service-orders — full round-trip. Only stores; sends nothing. */
  updateServiceOrderSettings(
    payload: UpdateServiceOrderSettingsPayload,
  ): Promise<ApiResult<ServiceOrderSettingsDto>> {
    return this.http.put<ServiceOrderSettingsDto>("/api/settings/service-orders", {
      body: payload,
    });
  }

  /**
   * POST /api/settings/service-orders/auto-generation
   * Company-wide switch for automatic serviceordre generation. `confirm: false` returns the
   * counts and writes nothing.
   */
  applyServiceOrderAutoGeneration(
    payload: ApplyAutoGenerationPayload,
  ): Promise<ApiResult<AutoGenerationResultDto>> {
    return this.http.post<AutoGenerationResultDto>("/api/settings/service-orders/auto-generation", {
      body: payload,
    });
  }

  /** GET /api/settings/service-orders/visit-notice-preview — sample e-mail text; sends nothing. */
  getServiceVisitNoticePreview(): Promise<ApiResult<ServiceVisitNoticePreviewDto>> {
    return this.http.get<ServiceVisitNoticePreviewDto>(
      "/api/settings/service-orders/visit-notice-preview",
    );
  }

  /**
   * GET /api/settings/economy
   * Cost types, DB target, stade band and default cost rate. Returns the defaults when the org
   * has not saved anything yet.
   */
  getEconomy(): Promise<ApiResult<EconomySettingsDto>> {
    return this.http.get<EconomySettingsDto>("/api/settings/economy");
  }

  /** PUT /api/settings/economy — full round-trip of the economy settings. */
  updateEconomy(payload: UpdateEconomySettingsPayload): Promise<ApiResult<EconomySettingsDto>> {
    return this.http.put<EconomySettingsDto>("/api/settings/economy", { body: payload });
  }

  /**
   * GET /api/settings/customer-score
   * Customer-score thresholds, prior and factors. Returns the defaults (with the two seeded
   * factors) when the org has not saved anything yet. Needs `settings.view`.
   */
  getCustomerScore(): Promise<ApiResult<CustomerScoreSettingsDto>> {
    return this.http.get<CustomerScoreSettingsDto>("/api/settings/customer-score");
  }

  /** PUT /api/settings/customer-score — full round-trip; needs `settings.editCompany`. */
  updateCustomerScore(payload: UpdateCustomerScoreSettingsPayload): Promise<ApiResult<CustomerScoreSettingsDto>> {
    return this.http.put<CustomerScoreSettingsDto>("/api/settings/customer-score", { body: payload });
  }

  /**
   * GET /api/settings/quote-pdf-defaults
   * The org's default quote-PDF layout, or `layout: null` when it has not set one.
   */
  getQuotePdfDefaults(): Promise<ApiResult<QuotePdfDefaultsSettingsDto>> {
    return this.http.get<QuotePdfDefaultsSettingsDto>("/api/settings/quote-pdf-defaults");
  }

  /**
   * PUT /api/settings/quote-pdf-defaults
   * Replaces the org default that new quotes are stamped with. Send null to clear it.
   * Existing quotes are never touched — the layout is copied at creation, not at render.
   */
  updateQuotePdfDefaults(
    payload: UpdateQuotePdfDefaultsSettingsPayload,
  ): Promise<ApiResult<QuotePdfDefaultsSettingsDto>> {
    return this.http.put<QuotePdfDefaultsSettingsDto>("/api/settings/quote-pdf-defaults", {
      body: payload,
    });
  }

  /**
   * POST /api/settings/integrations/nemhandel/register
   * Registers the active org for NemHandel (e-faktura) reception via Sproom and
   * persists the connection state. Returns the updated integration settings.
   * Replaces the legacy `registerNemHandel` Firebase Cloud Function.
   */
  registerNemHandel(
    payload: RegisterNemHandelPayload,
  ): Promise<ApiResult<IntegrationSettingsDto>> {
    return this.http.post<IntegrationSettingsDto>(
      "/api/settings/integrations/nemhandel/register",
      { body: payload },
    );
  }

  /**
   * POST /api/settings/integrations/nemhandel/disconnect
   * Clears NemHandel connection state for the active org. Idempotent.
   * Returns the updated integration settings.
   */
  disconnectNemHandel(): Promise<ApiResult<IntegrationSettingsDto>> {
    return this.http.post<IntegrationSettingsDto>(
      "/api/settings/integrations/nemhandel/disconnect",
    );
  }

  /**
   * Upload or replace the company logo.
   * The caller builds the FormData — pass it directly as the body.
   * Only JPEG and PNG are accepted; the backend enforces this.
   */
  uploadLogo(formData: FormData): Promise<ApiResult<CompanyLogoDto>> {
    return this.http.post<CompanyLogoDto>("/api/settings/logo", { body: formData });
  }

  /**
   * Get a short-lived signed URL for the current company logo.
   * Returns 404 when no blob-path logo is stored (no logo, or legacy Firebase URL).
   */
  getLogo(): Promise<ApiResult<CompanyLogoDto>> {
    return this.http.get<CompanyLogoDto>("/api/settings/logo");
  }

  /** Remove the current logo. Idempotent — succeeds even when no logo is set. */
  deleteLogo(): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>("/api/settings/logo");
  }

  /**
   * GET /api/settings/communication
   * Communication / customer-portal toggles for the active org. Replaces the
   * legacy Firestore `COMPANIES.settings.communication` read.
   */
  communication(): Promise<ApiResult<CommunicationSettingsDto>> {
    return this.http.get<CommunicationSettingsDto>("/api/settings/communication");
  }

  /**
   * PUT /api/settings/communication
   * Updates the per-feature communication toggles. The module-level `enabled`
   * flag is not settable here (owned by Marketplace activation). Returns the
   * updated settings.
   */
  updateCommunication(
    payload: UpdateCommunicationSettingsPayload,
  ): Promise<ApiResult<CommunicationSettingsDto>> {
    return this.http.put<CommunicationSettingsDto>(
      "/api/settings/communication",
      { body: payload },
    );
  }

  /**
   * PUT /api/settings/integrations/anthropic
   * Stores (or replaces) the org's own Anthropic API key for blueprint-analysis
   * BYO mode. The raw key is never returned — only a masked state.
   */
  setAnthropicKey(apiKey: string): Promise<ApiResult<AnthropicByoStateDto>> {
    return this.http.put<AnthropicByoStateDto>(
      "/api/settings/integrations/anthropic",
      { body: { apiKey } },
    );
  }

  /**
   * DELETE /api/settings/integrations/anthropic
   * Removes the org's BYO Anthropic key, reverting to the platform credit pool.
   */
  removeAnthropicKey(): Promise<ApiResult<AnthropicByoStateDto>> {
    return this.http.delete<AnthropicByoStateDto>(
      "/api/settings/integrations/anthropic",
    );
  }
}
